<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="3" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Edit Coin Seller / Merchant
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="3">
              <v-autocomplete
                ref="type"
                outlined
                v-model="form.type"
                :items="typeItems"
                label="Type *"
                prepend-inner-icon="mdi-account-badge"
                clearable
                :rules="[rules.required]"
                :error-messages="errors.type"
              ></v-autocomplete>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.unique_id"
                outlined
                type="number"
                label="Unique ID *"
                prepend-inner-icon="mdi-identifier"
                disabled
                hint="Unique ID cannot be changed"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.whatsapp_no"
                outlined
                type="tel"
                maxlength="15"
                label="WhatsApp Number *"
                prepend-inner-icon="mdi-whatsapp"
                :rules="[rules.required]"
                hint="Enter WhatsApp Number of the user"
                :error-messages="errors.whatsapp_no"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="3">
              <v-text-field
                v-model="currentBalance"
                outlined
                type="number"
                label="Current Balance"
                prepend-inner-icon="mdi-wallet"
                disabled
                hint="Current wallet balance"
                class="green--text"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-radio-group
                v-model="form.operation_type"
                row
                label="Balance Operation"
                :rules="[rules.required]"
              >
                <v-radio
                  label="Add Balance"
                  value="add"
                  color="success"
                ></v-radio>
                <v-radio
                  label="Deduct Balance"
                  value="deduct"
                  color="error"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="3">
              <v-text-field
                v-model="form.balance"
                outlined
                type="number"
                min="0"
                :label="
                  form.operation_type === 'deduct'
                    ? 'Deduct Amount'
                    : 'Add Amount'
                "
                prepend-inner-icon="mdi-bitcoin"
                :hint="
                  form.operation_type === 'deduct'
                    ? 'Enter amount to deduct from wallet balance'
                    : 'Enter amount to add to wallet balance'
                "
                :error-messages="errors.balance"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>
          <v-divider class="my-5"></v-divider>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="update"
          >
            Update
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Deduction Confirmation Dialog -->
    <v-dialog v-model="deductionDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon color="white" class="mr-2">mdi-alert</v-icon>
          Confirm Balance Deduction
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-h6 mb-3">
            You are about to deduct balance from this user's wallet.
          </div>
          <v-divider class="my-3"></v-divider>
          <div class="mb-2">
            <strong>Current Balance:</strong> {{ currentBalance }}
          </div>
          <div class="mb-2">
            <strong>Deduction Amount:</strong> {{ form.balance }}
          </div>
          <div class="mb-3">
            <strong>New Balance:</strong>
            <span
              :class="
                currentBalance - form.balance < 0 ? 'red--text' : 'green--text'
              "
            >
              {{ currentBalance - form.balance }}
            </span>
          </div>
          <v-alert type="warning" dense text class="mt-3">
            <strong>Warning:</strong> This action cannot be undone. Please
            confirm before proceeding.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="closeDeductionDialog"
            :disabled="isLoading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="isLoading"
            :disabled="isLoading"
            @click="confirmUpdate"
          >
            <v-icon left>mdi-check</v-icon>
            Confirm Deduction
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "CoinSellerMerchantEditPage",
  data() {
    return {
      form: {
        is_active: null,
        type: null,
        unique_id: null,
        whatsapp_no: null,
        balance: 0,
        operation_type: "add", // Default to add
      },
      currentBalance: 0,
      oldData: {},
      typeItems: [
        { text: "Coin Seller", value: "coin_seller" },
        { text: "Merchant", value: "merchant" },
      ],
      isLoading: false,
      deductionDialog: false,
      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  mounted() {
    this.getUserData();
  },
  methods: {
    async getUserData() {
      try {
        this.isLoading = true;
        const userId = this.$route.params.rechargeAgentId;

        let response = await axios.get(`/users/${userId}`);
        const user = response.data.data;

        this.oldData = { ...user };

        // Set form data
        this.form.unique_id = user.unique_id;
        this.form.whatsapp_no = user.whatsapp_no;
        this.form.balance = 0; // Default to 0 for adding balance
        this.form.operation_type = "add"; // Default to add

        // Get current wallet balance (wallet_type = 3)
        if (user.wallets && user.wallets.length > 0) {
          const wallet = user.wallets.find((w) => w.wallet_type == 3);
          this.currentBalance = wallet ? wallet.balance : 0;
        } else {
          this.currentBalance = 0;
        }

        // Determine type and active status based on is_coin_seller or is_merchant
        if (user.is_coin_seller == 1) {
          this.form.type = "coin_seller";
          this.form.is_active = true; // Active if coin seller
        } else if (user.is_merchant == 1) {
          this.form.type = "merchant";
          this.form.is_active = true; // Active if merchant
        } else {
          this.form.is_active = false; // Inactive if neither
        }

        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
        console.log("error", e);
        alert("Failed to load user data");
        this.$router.back();
      }
    },

    async update() {
      try {
        console.log(
          "type",
          this.form.type,
          "whatsapp_no",
          this.form.whatsapp_no
        );
        // Validate required fields
        if (!this.form.type || !this.form.whatsapp_no) {
          alert("Please fill all required fields.");
          return;
        }

        // Validate balance operation
        if (this.form.balance && this.form.balance > 0) {
          // Check if deduction operation
          if (this.form.operation_type === "deduct") {
            // Check if deduction amount is valid
            if (
              parseFloat(this.form.balance) > parseFloat(this.currentBalance)
            ) {
              alert("Deduction amount cannot exceed current balance.");
              return;
            }

            // Show confirmation dialog
            this.deductionDialog = true;
            return; // Wait for user to confirm
          }
        }

        // If not deduction or balance is 0, proceed directly
        await this.confirmUpdate();
      } catch (e) {
        console.log("error", e);
        alert("An error occurred during validation.");
      }
    },

    closeDeductionDialog() {
      this.deductionDialog = false;
    },

    async confirmUpdate() {
      try {
        this.isLoading = true;
        this.deductionDialog = false; // Close dialog if open

        const userId = this.$route.params.rechargeAgentId;

        // API call to update coin seller/merchant
        let response = await axios.patch(
          `/coin-seller-merchant/update/${userId}`,
          this.form
        );

        console.log("Response:", response.data);

        // Save timestamp log
        await this.saveUserTimestamp(this.oldData, this.form);

        // Success message
        alert(
          response.data.message || "Coin Seller/Merchant updated successfully!"
        );

        this.isLoading = false;
        this.$router.back();
      } catch (e) {
        this.isLoading = false;

        console.log("error", e);

        if (e.response && e.response.data) {
          if (e.response.data.errors) {
            let msg = e.response.data.message || "Validation error occurred.";
            alert(msg);
          } else if (e.response.data.message) {
            alert(e.response.data.message);
          } else {
            alert("Unexpected server error.");
          }
        } else {
          alert(
            "Network error: Cannot reach server. Please check if backend is running."
          );
        }
      }
    },

    async saveUserTimestamp(oldData, newData) {
      try {
        let form = {
          from_path: `/coinseller-merchants/${this.$route.params.rechargeAgentId}`,
          from_name: "CoinSellerMerchantEditPage",
          user_id: this.$store.state.user?.id,
          old_json: oldData ? JSON.stringify(oldData) : null,
          new_json: JSON.stringify(newData),
        };
        await axios.post(`/user_timestamps`, form);
      } catch (error) {
        console.log("timestamp error", error);
      }
    },
  },
};
</script>
